/**
 * Regra única de redirecionamento para a home nova (/turma, projeto Next via proxy).
 * Usada pelo Login e pela Home legada.
 *
 * - A decisão "tem turma?" vem sempre de uma chamada atual ao /classes (nunca de estado persistido).
 * - Erro no /classes não significa "sem turma": há retry antes de desistir.
 * - Anti-loop por contador: limita redirecionamentos em sequência sem travar a tela.
 */
import Bugsnag from '@bugsnag/js';
import { gateway } from './api';
import sleep from './sleep';

const REDIRECTS_KEY = 'turmaRedirects';
const REDIRECT_WINDOW_MS = 15000;
const MAX_REDIRECTS_IN_WINDOW = 3;

let redirecting = false;

export const getClassesQueryKey = () => [
  'myClasses',
  localStorage.getItem('idProfile') || '',
  localStorage.getItem('tenant') || '',
  localStorage.getItem('crCodeProfile') || ''
];

export const classesRetryDelay = attempt => Math.min(1000 * 2 ** attempt, 4000);

export const fetchMyClasses = async () => {
  const response = await gateway('/webbff/lit_app_web/classes');
  return Array.isArray(response) ? response : [];
};

export const fetchMyClassesWithRetry = async (retries = 2) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fetchMyClasses();
    } catch (error) {
      if (attempt >= retries) throw error;
      // eslint-disable-next-line no-await-in-loop
      await sleep(classesRetryDelay(attempt));
    }
  }
};

const readRecentRedirects = () => {
  try {
    const list = JSON.parse(window.sessionStorage.getItem(REDIRECTS_KEY) || '[]');
    const now = Date.now();
    return Array.isArray(list) ? list.filter(ts => now - ts < REDIRECT_WINDOW_MS) : [];
  } catch (e) {
    return [];
  }
};

export const canRedirectToTurma = () =>
  readRecentRedirects().length < MAX_REDIRECTS_IN_WINDOW;

const registerRedirect = () => {
  try {
    window.sessionStorage.setItem(
      REDIRECTS_KEY,
      JSON.stringify([...readRecentRedirects(), Date.now()])
    );
  } catch (e) {
    // ignore (ex.: storage bloqueado)
  }
};

/**
 * Garante uma turma selecionada válida para o /turma: se não houver seleção
 * ou se a turma salva não estiver mais na lista (ex.: turma encerrada), usa a primeira.
 */
export const ensureSelectedClass = classes => {
  if (!Array.isArray(classes) || classes.length === 0) return;
  const selectedCrCode = localStorage.getItem('selectedClassCrCode');
  const isValid =
    localStorage.getItem('selectedClassName') &&
    classes.some(item => item.organizationCode === selectedCrCode);
  if (isValid) return;

  const [first] = classes;
  if (first.name) localStorage.setItem('selectedClassName', first.name);
  if (first.organizationCode) localStorage.setItem('selectedClassCrCode', first.organizationCode);
};

export const notifyRedirectLoop = origin => {
  try {
    Bugsnag.notify(new Error('Redirecionamento para /turma bloqueado (anti-loop)'), event => {
      event.context = 'turmaRedirect';
      event.addMetadata('turmaRedirect', {
        origin,
        tenant: localStorage.getItem('tenant'),
        crCodeProfile: localStorage.getItem('crCodeProfile'),
        selectedClassCrCode: localStorage.getItem('selectedClassCrCode')
      });
    });
  } catch (e) {
    // ignore
  }
};

/** Full page load: /turma é outro projeto servido pelo proxy. Idempotente na mesma página. */
export const redirectToTurma = () => {
  if (redirecting) return;
  redirecting = true;
  registerRedirect();
  window.location.assign('/turma');
};
